import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const code = error.response?.data?.code;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const reason = (code === 'SESSION_EXPIRED' || code === 'SESSION_REQUIRED')
        ? 'session_expired'
        : 'unauthorized';
      window.location.href = `/login?reason=${reason}`;
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  heartbeat: () => api.post('/auth/heartbeat'),
  getSessions: () => api.get('/auth/sessions'),
  terminateSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
};

export const licenseConfigService = {
  get: () => api.get('/license-config'),
  update: (data) => api.put('/license-config', data),
};

export const patientService = {
  getAllPatients: () => api.get('/patients'),
  searchPatients: (q, limit = 20) => api.get(`/patients/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  getPatient: (id) => api.get(`/patients/${id}`),
  createPatient: (data, force = false) => api.post(`/patients${force ? '?force=true' : ''}`, data),
  updatePatient: (id, data) => api.put(`/patients/${id}`, data),
  deletePatient: (id) => api.delete(`/patients/${id}`),
  getPatientHistory: (id) => api.get(`/patients/${id}/history`),
  getLabReports: (id) => api.get(`/patients/${id}/lab-reports`),
  uploadLabReport: (id, formData) =>
    api.post(`/patients/${id}/lab-reports`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadPatientPhoto: (id, formData) =>
    api.post(`/patients/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deletePatientPhoto: (id) => api.delete(`/patients/${id}/photo`),
  uploadPatientAadhar: (id, formData) =>
    api.post(`/patients/${id}/aadhar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deletePatientAadhar: (id) => api.delete(`/patients/${id}/aadhar`),
};

export const labReportService = {
  getAll: (params) => api.get('/lab-reports', { params }),
  deleteReport: (id) => api.delete(`/lab-reports/${id}`),
};

export const appointmentService = {
  getAllAppointments: () => api.get('/appointments'),
  getTodayAppointments: () => api.get('/appointments/today'),
  getAppointment: (id) => api.get(`/appointments/${id}`),
  createAppointment: (data) => api.post('/appointments', data),
  updateAppointment: (id, data) => api.put(`/appointments/${id}`, data),
  deleteAppointment: (id) => api.delete(`/appointments/${id}`),
  getAppointmentsByDoctor: (doctorId) => api.get(`/appointments/doctor/${doctorId}`),
  getAppointmentsByPatient: (patientId) => api.get(`/appointments/patient/${patientId}`),
};

export const prescriptionService = {
  getPrescriptions: (patientId) => api.get(`/prescriptions/patient/${patientId}`),
  createPrescription: (data) => api.post('/prescriptions', data),
  getPrescriptionById: (id) => api.get(`/prescriptions/${id}`),
  getLastPrescription: (patientId) => api.get(`/prescriptions/patient/${patientId}/last`),
  getByAppointmentId: (appointmentId) => api.get(`/prescriptions/appointment/${appointmentId}`),
};

export const medicineService = {
  getAllMedicines: () => api.get('/medicines'),
  createMedicine: (data) => api.post('/medicines', data),
  updateMedicine: (id, data) => api.put(`/medicines/${id}`, data),
  deleteMedicine: (id) => api.delete(`/medicines/${id}`),
  getMedicinesByHotkey: (hotkey) => api.get(`/medicines/hotkey/${hotkey}`),
};

export const adviceService = {
  getAllAdvices: () => api.get('/advices'),
  createAdvice: (data) => api.post('/advices', data),
  updateAdvice: (id, data) => api.put(`/advices/${id}`, data),
  deleteAdvice: (id) => api.delete(`/advices/${id}`),
  getAdvicesByHotkey: (hotkey) => api.get(`/advices/hotkey/${hotkey}`),
};

export const userService = {
  getAllUsers: () => api.get('/users'),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  getDoctors: () => api.get('/users/role/doctor'),
};

export const printSettingsService = {
  getSettings: () => api.get('/print-settings'),
  updateSettings: (data) => api.put('/print-settings', data),
};

export const homePageSettingsService = {
  getSettings: () => api.get('/home-settings'),
  updateSettings: (data) => api.put('/home-settings', data),
  getDoctors: () => api.get('/home-settings/doctors'),
};

export const investigationService = {
  getAllInvestigations: () => api.get('/investigations'),
  createInvestigation: (data) => api.post('/investigations', data),
  updateInvestigation: (id, data) => api.put(`/investigations/${id}`, data),
  deleteInvestigation: (id) => api.delete(`/investigations/${id}`),
};

export const referDoctorService = {
  getAllReferDoctors: () => api.get('/referdoctors'),
  createReferDoctor: (data) => api.post('/referdoctors', data),
  updateReferDoctor: (id, data) => api.put(`/referdoctors/${id}`, data),
  deleteReferDoctor: (id) => api.delete(`/referdoctors/${id}`),
};

export const translateService = {
  translate: (texts, targetLang) => api.post('/translate', { texts, targetLang }),
};

export const revenueAnalyticsService = {
  getSummary:      (from, to) => api.get('/revenueanalytics/summary',      { params: { from, to } }),
  getTrend:        (from, to) => api.get('/revenueanalytics/trend',        { params: { from, to } }),
  getPaymentModes: (from, to) => api.get('/revenueanalytics/payment-modes', { params: { from, to } }),
  getByService:    (from, to) => api.get('/revenueanalytics/by-service',   { params: { from, to } }),
  getByType:       (from, to) => api.get('/revenueanalytics/by-type',      { params: { from, to } }),
  getByDoctor:     (from, to) => api.get('/revenueanalytics/by-doctor',    { params: { from, to } }),
  getRecentBills:  (from, to) => api.get('/revenueanalytics/recent-bills', { params: { from, to } }),
};

export const serviceRateService = {
  getAll: () => api.get('/servicerates'),
  create: (data) => api.post('/servicerates', data),
  update: (id, data) => api.put(`/servicerates/${id}`, data),
  delete: (id) => api.delete(`/servicerates/${id}`),
};

export const billingService = {
  createBill: (data) => api.post('/bills', data),
  getAllBills: () => api.get('/bills'),
  getBillsByPatient: (patientId) => api.get(`/bills/patient/${patientId}`),
  getBillsByAdmission: (admissionId) => api.get(`/bills/admission/${admissionId}`),
  getBill: (id) => api.get(`/bills/${id}`),
};

export const doctorPerformanceService = {
  getSummary:           (doctorId, from, to) => api.get('/doctorperformance/summary',             { params: { doctorId, from, to } }),
  getTrend:             (doctorId, from, to) => api.get('/doctorperformance/trend',               { params: { doctorId, from, to } }),
  getPatientTypes:      (doctorId, from, to) => api.get('/doctorperformance/patient-types',       { params: { doctorId, from, to } }),
  getStatusBreakdown:   (doctorId, from, to) => api.get('/doctorperformance/status-breakdown',    { params: { doctorId, from, to } }),
  getTopMedicines:      (doctorId, from, to) => api.get('/doctorperformance/top-medicines',       { params: { doctorId, from, to } }),
  getComparison:        (from, to)           => api.get('/doctorperformance/comparison',          { params: { from, to } }),
  getRecentAppointments:(doctorId, from, to) => api.get('/doctorperformance/recent-appointments', { params: { doctorId, from, to } }),
};

export const dischargeSummaryService = {
  save: (data) => api.post('/dischargesummary', data),
  getByAppointment: (appointmentId) => api.get(`/dischargesummary/appointment/${appointmentId}`),
};

export const mediaService = {
  getAllMedia: () => api.get('/media'),
  uploadMedia: (formData) =>
    api.post('/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateMedia: (id, data) => api.put(`/media/${id}`, data),
  deleteMedia: (id) => api.delete(`/media/${id}`),
};

export const issuedCertificateService = {
  save:       (data)    => api.post('/issued-certificates', data),
  getAll:     (params)  => api.get('/issued-certificates', { params }),
  getByCertNo:(certNo)  => api.get(`/issued-certificates/${certNo}`),
};

export const attendanceService = {
  getAll:   (params) => api.get('/attendance', { params }),
  upsert:   (data)   => api.post('/attendance', data),
  bulkMark: (data)   => api.post('/attendance/bulk', data),
  delete:   (id)     => api.delete(`/attendance/${id}`),
};

export const leaveService = {
  getAll:  (params)   => api.get('/leaverequests', { params }),
  getMy:   (params)   => api.get('/leaverequests/my', { params }),
  create:  (data)     => api.post('/leaverequests', data),
  update:  (id, data) => api.put(`/leaverequests/${id}`, data),
  delete:  (id)       => api.delete(`/leaverequests/${id}`),
};

export const salaryStructureService = {
  getAll:    ()       => api.get('/salarystructure'),
  getByUser: (userId) => api.get(`/salarystructure/${userId}`),
  upsert:    (data)   => api.post('/salarystructure', data),
};

export const salarySlipService = {
  getAll:   (params) => api.get('/salaryslips', { params }),
  generate: (data)   => api.post('/salaryslips/generate', data),
  markPaid: (id)     => api.put(`/salaryslips/${id}/mark-paid`),
  delete:   (id)     => api.delete(`/salaryslips/${id}`),
};

export const doctorFavouriteService = {
  getMedicines: ()             => api.get('/doctorfavourites/medicines'),
  addMedicine:  (id)           => api.post(`/doctorfavourites/medicines/${id}`),
  removeMedicine: (id)         => api.delete(`/doctorfavourites/medicines/${id}`),
  updateMedicineHotkey: (id, hotkey) => api.put(`/doctorfavourites/medicines/${id}/hotkey`, { hotkey }),
  getAdvices:   ()             => api.get('/doctorfavourites/advices'),
  addAdvice:    (id)           => api.post(`/doctorfavourites/advices/${id}`),
  removeAdvice: (id)           => api.delete(`/doctorfavourites/advices/${id}`),
  updateAdviceHotkey: (id, hotkey) => api.put(`/doctorfavourites/advices/${id}/hotkey`, { hotkey }),
};

export const expenseService = {
  getAll:        (params) => api.get('/expenses', { params }),
  getSummary:    (params) => api.get('/expenses/summary', { params }),
  getMonthlyTrend: (year) => api.get('/expenses/monthly-trend', { params: { year } }),
  create:        (data)   => api.post('/expenses', data),
  update:        (id, data) => api.put(`/expenses/${id}`, data),
  delete:        (id)     => api.delete(`/expenses/${id}`),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/expenses/${id}/attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAttachment: (id) => api.delete(`/expenses/${id}/attachment`),
};

export const cabinService = {
  getAll:   ()           => api.get('/cabins'),
  create:   (data)       => api.post('/cabins', data),
  update:   (id, data)   => api.put(`/cabins/${id}`, data),
  delete:   (id)         => api.delete(`/cabins/${id}`),
};

export const cabinAssignmentService = {
  getAssignments: (date)   => api.get('/cabinassignments', { params: { date } }),
  create:         (data)   => api.post('/cabinassignments', data),
  confirmAll:     (date)   => api.put('/cabinassignments/confirm-all', { date }),
  unconfirmAll:   (date)   => api.put('/cabinassignments/unconfirm-all', { date }),
  delete:         (id)     => api.delete(`/cabinassignments/${id}`),
};

export const displayService = {
  getQueue: () => api.get('/display/queue'),
};

export const notificationService = {
  getAll:       ()       => api.get('/notifications'),
  getMy:        ()       => api.get('/notifications/my'),
  getUnreadCount: ()     => api.get('/notifications/unread-count'),
  create:       (data)   => api.post('/notifications', data),
  update:       (id, data) => api.put(`/notifications/${id}`, data),
  delete:       (id)     => api.delete(`/notifications/${id}`),
  dismiss:      (id)     => api.post(`/notifications/${id}/dismiss`),
  dismissAll:   ()       => api.post('/notifications/dismiss-all'),
};

export const otRoomService = {
  getAll:  ()           => api.get('/ot-rooms'),
  create:  (data)       => api.post('/ot-rooms', data),
  update:  (id, data)   => api.put(`/ot-rooms/${id}`, data),
  delete:  (id)         => api.delete(`/ot-rooms/${id}`),
};

export const surgeryService = {
  getAll:       (params)    => api.get('/surgeries', { params }),
  getToday:     ()          => api.get('/surgeries/today'),
  getById:      (id)        => api.get(`/surgeries/${id}`),
  getByPatient: (patientId) => api.get(`/surgeries/patient/${patientId}`),
  create:       (data)      => api.post('/surgeries', data),
  update:       (id, data)  => api.put(`/surgeries/${id}`, data),
  updateStatus: (id, status) => api.patch(`/surgeries/${id}/status`, { status }),
  delete:       (id)        => api.delete(`/surgeries/${id}`),
};

export const consentFormService = {
  getAll:    (surgeryId)        => api.get(`/surgeries/${surgeryId}/consents`),
  getById:   (surgeryId, id)    => api.get(`/surgeries/${surgeryId}/consents/${id}`),
  create:    (surgeryId, data)  => api.post(`/surgeries/${surgeryId}/consents`, data),
  update:    (surgeryId, id, data) => api.put(`/surgeries/${surgeryId}/consents/${id}`, data),
  delete:    (surgeryId, id)    => api.delete(`/surgeries/${surgeryId}/consents/${id}`),
};

export const consentFormDocumentService = {
  getAll:  (surgeryId, consentId) =>
    api.get(`/surgeries/${surgeryId}/consents/${consentId}/documents`),
  upload:  (surgeryId, consentId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/surgeries/${surgeryId}/consents/${consentId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete:  (surgeryId, consentId, docId) =>
    api.delete(`/surgeries/${surgeryId}/consents/${consentId}/documents/${docId}`),
};

export const preOpChecklistService = {
  get:    (surgeryId)       => api.get(`/surgeries/${surgeryId}/pre-op-checklist`),
  upsert: (surgeryId, data) => api.put(`/surgeries/${surgeryId}/pre-op-checklist`, data),
};

export const preOpChecklistItemService = {
  getAll:  ()           => api.get('/pre-op-checklist-items'),
  create:  (data)       => api.post('/pre-op-checklist-items', data),
  update:  (id, data)   => api.put(`/pre-op-checklist-items/${id}`, data),
  delete:  (id)         => api.delete(`/pre-op-checklist-items/${id}`),
};

export const surgeryTypeService = {
  getAll:  ()           => api.get('/surgery-types'),
  create:  (data)       => api.post('/surgery-types', data),
  update:  (id, data)   => api.put(`/surgery-types/${id}`, data),
  delete:  (id)         => api.delete(`/surgery-types/${id}`),
};

export const surgeryTypeMappingService = {
  get:    (surgeryTypeId)       => api.get(`/surgery-types/${surgeryTypeId}/mapping`),
  upsert: (surgeryTypeId, data) => api.put(`/surgery-types/${surgeryTypeId}/mapping`, data),
};

export const consentFormTemplateService = {
  getAll:    ()           => api.get('/consent-form-templates'),
  getById:   (id)         => api.get(`/consent-form-templates/${id}`),
  create:    (data)       => api.post('/consent-form-templates', data),
  update:    (id, data)   => api.put(`/consent-form-templates/${id}`, data),
  delete:    (id)         => api.delete(`/consent-form-templates/${id}`),
  convertDocx: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/consent-form-templates/convert-docx', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ── IPD Phase 2 ───────────────────────────────────────────────────────────────
export const ipdVitalsService = {
  getAll:  (admissionId)        => api.get(`/ipd/admissions/${admissionId}/vitals`),
  create:  (admissionId, data)  => api.post(`/ipd/admissions/${admissionId}/vitals`, data),
  delete:  (admissionId, id)    => api.delete(`/ipd/admissions/${admissionId}/vitals/${id}`),
};

export const ipdClinicalNoteService = {
  getAll:  (admissionId)              => api.get(`/ipd/admissions/${admissionId}/clinical-notes`),
  create:  (admissionId, data)        => api.post(`/ipd/admissions/${admissionId}/clinical-notes`, data),
  update:  (admissionId, id, data)    => api.put(`/ipd/admissions/${admissionId}/clinical-notes/${id}`, data),
  delete:  (admissionId, id)          => api.delete(`/ipd/admissions/${admissionId}/clinical-notes/${id}`),
};

export const ipdMedicationService = {
  getAll:       (admissionId)         => api.get(`/ipd/admissions/${admissionId}/medications`),
  create:       (admissionId, data)   => api.post(`/ipd/admissions/${admissionId}/medications`, data),
  discontinue:  (admissionId, id)     => api.put(`/ipd/admissions/${admissionId}/medications/${id}/discontinue`),
  delete:       (admissionId, id)     => api.delete(`/ipd/admissions/${admissionId}/medications/${id}`),
  administer:   (admissionId, id, data) => api.post(`/ipd/admissions/${admissionId}/medications/${id}/administer`, data),
};

export const ipdDischargeSummaryService = {
  get:  (admissionId)       => api.get(`/ipd/admissions/${admissionId}/discharge-summary`),
  save: (admissionId, data) => api.post(`/ipd/admissions/${admissionId}/discharge-summary`, data),
};

export const ipdInvestigationService = {
  getAll:     (admissionId)        => api.get(`/ipd/admissions/${admissionId}/investigations`),
  order:      (admissionId, data)  => api.post(`/ipd/admissions/${admissionId}/investigations`, data),
  enterResult:(admissionId, id, data) => api.put(`/ipd/admissions/${admissionId}/investigations/${id}/result`, data),
  cancel:     (admissionId, id)    => api.put(`/ipd/admissions/${admissionId}/investigations/${id}/cancel`),
  delete:     (admissionId, id)    => api.delete(`/ipd/admissions/${admissionId}/investigations/${id}`),
};

export const ipdFluidBalanceService = {
  getAll: (admissionId)       => api.get(`/ipd/admissions/${admissionId}/fluid-balance`),
  add:    (admissionId, data) => api.post(`/ipd/admissions/${admissionId}/fluid-balance`, data),
  delete: (admissionId, id)   => api.delete(`/ipd/admissions/${admissionId}/fluid-balance/${id}`),
};

// ── IPD Module ────────────────────────────────────────────────────────────────
export const wardService = {
  getAll:     ()            => api.get('/wards'),
  getById:    (id)          => api.get(`/wards/${id}`),
  create:     (data)        => api.post('/wards', data),
  update:     (id, data)    => api.put(`/wards/${id}`, data),
  delete:     (id)          => api.delete(`/wards/${id}`),
  getBeds:    (wardId)      => api.get(`/wards/${wardId}/beds`),
};

export const bedService = {
  create:       (data)          => api.post('/beds', data),
  update:       (id, data)      => api.put(`/beds/${id}`, data),
  updateStatus: (id, status)    => api.put(`/beds/${id}/status`, { status }),
  delete:       (id)            => api.delete(`/beds/${id}`),
};

export const ipdService = {
  dashboard:   ()              => api.get('/ipd/dashboard'),
  getAll:      (params)        => api.get('/ipd/admissions', { params }),
  getById:     (id)            => api.get(`/ipd/admissions/${id}`),
  create:      (data)          => api.post('/ipd/admissions', data),
  update:      (id, data)      => api.put(`/ipd/admissions/${id}`, data),
  discharge:   (id, data)      => api.put(`/ipd/admissions/${id}/discharge`, data),
  transferBed: (id, data)      => api.put(`/ipd/admissions/${id}/transfer-bed`, data),
  getBedTransfers: (id)        => api.get(`/ipd/admissions/${id}/bed-transfers`),
};

export const insurerService = {
  getAll:  ()           => api.get('/insurers'),
  create:  (data)       => api.post('/insurers', data),
  update:  (id, data)   => api.put(`/insurers/${id}`, data),
  delete:  (id)         => api.delete(`/insurers/${id}`),
};

export const tpaService = {
  getAll:  ()           => api.get('/tpas'),
  create:  (data)       => api.post('/tpas', data),
  update:  (id, data)   => api.put(`/tpas/${id}`, data),
  delete:  (id)         => api.delete(`/tpas/${id}`),
};

export const insuranceDocumentTypeService = {
  getAll:  ()           => api.get('/insurance-document-types'),
  create:  (data)       => api.post('/insurance-document-types', data),
  update:  (id, data)   => api.put(`/insurance-document-types/${id}`, data),
  delete:  (id)         => api.delete(`/insurance-document-types/${id}`),
};

export const insuranceCaseService = {
  getAll:         (params)     => api.get('/insurance-cases', { params }),
  getById:        (id)         => api.get(`/insurance-cases/${id}`),
  getByAdmission: (admissionId) => api.get(`/insurance-cases/admission/${admissionId}`),
  create:         (data)       => api.post('/insurance-cases', data),
  update:         (id, data)   => api.put(`/insurance-cases/${id}`, data),
  updateStatus:   (id, data)   => api.patch(`/insurance-cases/${id}/status`, data),
  updateChecklist: (id, data)  => api.put(`/insurance-cases/${id}/checklist`, data),
  getPacket:      (id)         => api.get(`/insurance-cases/${id}/packet`),
  uploadDocument: (caseId, docId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/insurance-cases/${caseId}/documents/${docId}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocumentFile: (caseId, docId) => api.delete(`/insurance-cases/${caseId}/documents/${docId}/file`),
};

export const insuranceDocumentTemplateService = {
  getAll:  ()           => api.get('/insurance-document-templates'),
  getById: (id)         => api.get(`/insurance-document-templates/${id}`),
  create:  (data)       => api.post('/insurance-document-templates', data),
  update:  (id, data)   => api.put(`/insurance-document-templates/${id}`, data),
  delete:  (id)         => api.delete(`/insurance-document-templates/${id}`),
};

export default api;
